import { createAsyncThunk } from '@reduxjs/toolkit';
import { iamClient, minerClient } from 'api/clients';
import camelCase from 'lodash/camelCase';
import {
  addMinerTypesSuccess,
  deleteMinerSuccess,
  fetchAllMinersSuccess,
  fetchDevicePageFailure,
  fetchDevicePageStart,
  fetchDevicePageSuccess,
  fetchMinerConsumptionSuccess,
  requestFailure,
  requestStart,
  setMinersCount
} from './miner.reducer';

// Rows from GET tenant/devices are already flat and camelCase; they carry everything the
// legacy composite rows did (plus customer/contract/pool names) so existing consumers of
// state.miner.miners keep working. `contractId` is the one name the table columns rely on.
const toLegacyRow = row => ({ ...row, contractId: row.contractId ?? null });

// One server-side page for the All Devices table.
export const fetchDevicePageThunk = createAsyncThunk(
  'miner/fetchDevicePage',
  async (params, { dispatch }) => {
    dispatch(fetchDevicePageStart());
    try {
      const data = await iamClient.getTenantDevices(params);
      dispatch(fetchDevicePageSuccess(data));
      return data;
    } catch (error) {
      dispatch(fetchDevicePageFailure(error.toString()));
      throw error;
    }
  }
);

// The WHOLE fleet, for the pages that need it in memory (location/pool/contract rollups,
// owner pickers). Pages through GET tenant/devices in 1000-row chunks. Only call this via
// useEnsureAllMiners so it runs at most once per session; at six-figure fleets this is
// deliberately not on the startup path.
const ALL_MINERS_CHUNK = 1000;
export const fetchAllMinersThunk = createAsyncThunk(
  'miner/fetchAllMiners',
  async (_, { dispatch }) => {
    dispatch(requestStart());
    try {
      const rows = [];
      let page = 1;
      let fleet = null;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const data = await iamClient.getTenantDevices({
          page,
          pageSize: ALL_MINERS_CHUNK
        });
        fleet = fleet ?? data;
        rows.push(...(data.devices ?? []).map(toLegacyRow));
        if (
          rows.length >= (data.totalCount ?? 0) ||
          !(data.devices ?? []).length
        )
          break;
        page += 1;
      }
      dispatch(fetchAllMinersSuccess(rows));
      if (fleet) {
        dispatch(
          setMinersCount({
            activeMiners: fleet.fleetOnline ?? 0,
            inactiveMiners: fleet.fleetOffline ?? 0
          })
        );
      }
      return rows;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      throw error;
    }
  }
);

// Get miner consumption
export const getMinerConsumptionThunk = createAsyncThunk(
  'miner/getMinerConsumptionThunk',
  async (id, { dispatch }) => {
    dispatch(requestStart());

    try {
      const { Calculations } = await minerClient.getMinerConsumption(id);
      const mappedData = Calculations.map(calc => {
        const newObj = {};
        Object.keys(calc).forEach(key => {
          newObj[camelCase(key)] = calc[key];
        });
        return newObj;
      });
      dispatch(fetchMinerConsumptionSuccess(mappedData));
      return mappedData;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
    }
  }
);

// Update miner thunk
export const updateMinerThunk = createAsyncThunk(
  'miner/updateMinerThunk',
  async (
    { id, updateFieldKey, value, locationName },
    { dispatch, rejectWithValue }
  ) => {
    dispatch(requestStart());

    try {
      await minerClient.updateMinerField(id, updateFieldKey, value);

      return { id, updateFieldKey, value, locationName };
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      return rejectWithValue(error.toString());
    }
  }
);

// Update connectedPool thunk
export const updateConnectedPoolThunk = createAsyncThunk(
  'miner/updateConnectedPoolThunk',
  async ({ id, poolId }, { dispatch, rejectWithValue }) => {
    dispatch(requestStart());

    try {
      await minerClient.updateMinerConnectedPool(id, poolId);

      return { id, poolId };
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      return rejectWithValue(error.toString());
    }
  }
);

// Update miner owner thunk
export const updateMinerOwnerThunk = createAsyncThunk(
  'miner/updateMinerOwnerThunk',
  async (
    { minerId, newOwnerId, contractId },
    { dispatch, rejectWithValue, getState }
  ) => {
    dispatch(requestStart());

    const state = getState();
    const oldMiner = state.miner.miners.find(m => m.id === minerId);

    if (!oldMiner) {
      return rejectWithValue('Miner not found');
    }

    const oldOwnerId = oldMiner.userId;

    try {
      const res = await minerClient.updateMinerOwner(
        minerId,
        newOwnerId,
        contractId
      );

      console.log(res);

      return { minerId, oldOwnerId, newOwnerId };
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      return rejectWithValue(error.toString());
    }
  }
);

// Adding new miner
export const addNewMinerThunk = createAsyncThunk(
  'miner/addNewMinerThunk',
  async (formData, { dispatch }) => {
    dispatch(requestStart());
    const newData = { ...formData };
    delete newData.minerTypeName;
    delete newData.locationName;

    try {
      const data = await minerClient.addMiner(newData);
      return { data, formData };
    } catch (error) {
      dispatch(requestFailure(error.toString()));
    }
  }
);

// Get miner types
export const getMinerTypesThunk = createAsyncThunk(
  'miner/getMinerTypesThunk',
  async (_, { dispatch }) => {
    dispatch(requestStart());

    try {
      const { minerTypes } = await minerClient.getMinerTypes();
      dispatch(addMinerTypesSuccess(minerTypes));
      return minerTypes;
    } catch (error) {
      dispatch(requestFailure(error.toString()));
    }
  }
);

// Delete miner thunk
export const deleteMinerThunk = createAsyncThunk(
  'miner/deleteMinerThunk',
  async ({ id }, { dispatch, rejectWithValue }) => {
    dispatch(requestStart());

    try {
      const res = await minerClient.deleteMiner(id);

      if (res.success) {
        dispatch(deleteMinerSuccess(id));
      }
      return { id };
    } catch (error) {
      dispatch(requestFailure(error.toString()));
      return rejectWithValue(error.toString());
    }
  }
);
